import { useId, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { cn } from "./cn";
import { formatDayMonth } from "./format";

const W = 1000;
const H = 300;

/** One line on the chart. */
export type UsageSeries = {
  /** Stable key; also the key each point's `values` map is read with. */
  key: string;
  label: string;
  /** Gradient token the line and its fill use. Cycles by index if omitted. */
  tone?: "brand" | "teal";
};

/** One column of the window — a day, an hour, whatever the caller bucketed by. */
export type UsagePoint = {
  /** ISO timestamp; only its date part is rendered. */
  date: string;
  /** Value per series key. A missing key reads as zero. */
  values: Record<string, number>;
};

const TONE_COLORS: Record<"brand" | "teal", string> = {
  brand: "var(--color-brand)",
  teal: "var(--color-teal-strong)",
};

const TONE_CYCLE = ["brand", "teal"] as const;

/** The design named a colour per fixture application; here the palette cycles. */
function seriesColor(item: UsageSeries, index: number) {
  return TONE_COLORS[item.tone ?? TONE_CYCLE[index % TONE_CYCLE.length]!];
}

function buildPath(values: number[], max: number, close: boolean) {
  const step = values.length > 1 ? W / (values.length - 1) : W;
  const points = values.map((value, index) => {
    const x = index * step;
    const y = H - (value / max) * H;
    return [x, y] as const;
  });

  // Catmull-Rom style smoothing keeps the line readable at 30 daily points.
  const first = points[0]!;
  let d = `M ${first[0]} ${first[1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i]!;
    const [x1, y1] = points[i + 1]!;
    const cx = (x0 + x1) / 2;
    d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`;
  }
  if (close) d += ` L ${W} ${H} L 0 ${H} Z`;
  return d;
}

/**
 * The design's usage chart: a hand-rolled responsive SVG with no charting
 * library, so the bundle carries nothing for one screen's worth of lines.
 *
 * Two things the design could hard-code and this port cannot. Its `series`
 * was a module-scope constant naming the two fixture applications; here it is
 * a prop, because the number of applications is whatever the account owns.
 * And its gradient ids were literals (`fill-app-a`), which collide the moment
 * two charts share a page — an SVG `url(#id)` resolves document-wide — so the
 * ids are namespaced with `useId()`.
 */
export function UsageChart({
  series,
  points,
  unitLabel = "requests per day",
  emptyLabel = "No usage recorded in this period.",
  className,
}: {
  series: UsageSeries[];
  points: UsagePoint[];
  /** Right-hand caption above the plot. */
  unitLabel?: string;
  emptyLabel?: string;
  className?: string;
}) {
  const gradientId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const lines = useMemo(
    () =>
      series.map((item, index) => ({
        ...item,
        color: seriesColor(item, index),
        values: points.map((point) => point.values[item.key] ?? 0),
      })),
    [series, points],
  );

  const max = useMemo(() => {
    const peak = Math.max(1, ...lines.flatMap((line) => line.values));
    return Math.ceil(peak / 10) * 10 || 10;
  }, [lines]);

  const paths = useMemo(
    () =>
      lines.map((line) => ({
        ...line,
        line: line.values.length ? buildPath(line.values, max, false) : "",
        area: line.values.length ? buildPath(line.values, max, true) : "",
      })),
    [lines, max],
  );

  // A one-point window has no span to interpolate across; dividing by it would
  // put every marker at NaN%, which renders as a stack in the top-left corner.
  const span = Math.max(1, points.length - 1);

  function onMove(event: ReactPointerEvent<HTMLDivElement>) {
    const box = wrapper.current?.getBoundingClientRect();
    if (!box) return;
    const ratio = (event.clientX - box.left) / box.width;
    const index = Math.round(ratio * span);
    setHover(Math.min(points.length - 1, Math.max(0, index)));
  }

  const legend = (
    <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2">
      {series.map((item, index) => (
        <span key={item.key} className="flex items-center gap-2 text-xs text-ink-3">
          <span
            className="size-2 rounded-full"
            style={{ background: seriesColor(item, index) }}
            aria-hidden
          />
          {item.label}
        </span>
      ))}
      <span className="ml-auto text-2xs text-ink-4">{unitLabel}</span>
    </div>
  );

  if (points.length === 0) {
    return (
      <div className={className}>
        {legend}
        <p className="py-10 text-center text-xs text-ink-4">{emptyLabel}</p>
      </div>
    );
  }

  const active = hover === null ? null : points[hover];

  return (
    <div className={className}>
      {legend}

      <div
        ref={wrapper}
        className="relative h-44 w-full touch-pan-y select-none sm:h-56"
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {/* Horizontal gridlines, drawn in flow so they never distort. */}
        <div aria-hidden className="absolute inset-0 flex flex-col justify-between">
          {[0, 1, 2, 3, 4].map((line) => (
            <span
              key={line}
              className={cn("h-px w-full", line === 4 ? "bg-line" : "bg-line-soft")}
            />
          ))}
        </div>

        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 size-full overflow-visible"
          role="img"
          aria-label={`${unitLabel} over ${points.length} points, by application`}
        >
          <defs>
            {paths.map((item) => (
              <linearGradient
                key={item.key}
                id={`${gradientId}-${item.key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={item.color} stopOpacity="0.28" />
                <stop offset="100%" stopColor={item.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>

          {paths.map((item) => (
            <path
              key={`a-${item.key}`}
              d={item.area}
              fill={`url(#${gradientId}-${item.key})`}
            />
          ))}
          {paths.map((item) => (
            <path
              key={`l-${item.key}`}
              d={item.line}
              fill="none"
              stroke={item.color}
              strokeWidth={2}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {hover !== null ? (
            <line
              x1={(hover / span) * W}
              x2={(hover / span) * W}
              y1={0}
              y2={H}
              stroke="var(--color-ink-4)"
              strokeWidth={1}
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
        </svg>

        {/* Hover markers positioned with percentages so they stay circular. */}
        {active && hover !== null
          ? paths.map((item) => (
              <span
                key={item.key}
                aria-hidden
                className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface"
                style={{
                  background: item.color,
                  left: `${(hover / span) * 100}%`,
                  top: `${100 - ((active.values[item.key] ?? 0) / max) * 100}%`,
                }}
              />
            ))
          : null}

        {active && hover !== null ? (
          <div
            className="pointer-events-none absolute -top-2 z-10 w-max -translate-x-1/2 rounded-field border border-line bg-surface-raised px-3 py-2 shadow-raised"
            style={{ left: `${Math.min(88, Math.max(12, (hover / span) * 100))}%` }}
          >
            <p className="text-2xs font-semibold text-ink-4">
              {formatDayMonth(active.date)}
            </p>
            {paths.map((item) => (
              <p key={item.key} className="tnum mt-0.5 flex items-center gap-2 text-xs">
                <span
                  className="size-1.5 rounded-full"
                  style={{ background: item.color }}
                />
                <span className="text-ink-3">{item.label}</span>
                <span className="ml-auto font-semibold text-ink">
                  {active.values[item.key] ?? 0}
                </span>
              </p>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-2 flex justify-between text-2xs text-ink-4">
        <span>{formatDayMonth(points[0]!.date)}</span>
        <span className="hidden sm:inline">
          {formatDayMonth(points[Math.floor(points.length / 2)]!.date)}
        </span>
        <span>{formatDayMonth(points[points.length - 1]!.date)}</span>
      </div>
    </div>
  );
}
